import React from 'react';
import classes from './Transactions.module.scss';
import { Button, Divider, Flex, Modal, Typography } from 'antd';
import TransactionIcon from '../components/TransactionIcon';
import { useIntl } from 'react-intl';
import { CloseOutlined } from '@ant-design/icons';
import { Transaction, TransactionDetails, TransactionStatus, TransactionType } from 'widgets/transactions/model/types';
import { ActivityIndicator, CenteredContainer } from 'shared/ui';
import _ from 'lodash';
import CardIcon from 'shared/ui/CardIcon/CardIcon';
import { useSelector } from 'react-redux';
import { RootState } from 'shared/config/store/store';
import { useMessage } from 'shared/libs/hooks/useMessage/useMessage';
import { downloadTransactionPdf, useLazyGetTransactionPdfQuery } from '../api/transactions';
import { Account } from 'entities/account/model/types';
import { getEMICurrentAccountObject } from 'shared/config/store/reducers/accountsSlice';
import { getCurrentEmi } from 'shared/config/store/selectors/app';
import { getUserMeta } from 'shared/config/store/reducers/userSlice';
import { formatTransactionAmount, TransactionAmountPresentation } from '../lib/transactionAmountPresentation';

const { Text, Title } = Typography;

type TransactionDetailsProps = {
  loading: boolean;
  transaction?: Transaction;
  transactionDetails?: TransactionDetails;
  onClose: () => unknown;
  amountPresentation?: TransactionAmountPresentation;
};

const TransactionDetailsModal: React.FC<TransactionDetailsProps> = ({
  loading,
  transaction,
  transactionDetails,
  onClose,
  amountPresentation = 'default',
}) => {
  const { formatMessage: t } = useIntl();
  const { showError, showSuccess } = useMessage();
  const [getTransactionPdf] = useLazyGetTransactionPdfQuery();
  const currentEmi = useSelector(getCurrentEmi);
  const currentAccount = useSelector((state: RootState) =>
    getEMICurrentAccountObject(state, currentEmi),
  ) as Account | undefined;
  const token = useSelector((state: RootState) => state.user.token);
  const userMeta = useSelector(getUserMeta);
  const language = useSelector((state: RootState) => state.app.language);
  const effectiveType = transactionDetails?.type || transaction?.type;
  const payerName =
    transactionDetails?.document?.sender_name ||
    transactionDetails?.document?.recipient_name ||
    transaction?.recipientName;
  const isMerchantType =
    effectiveType === TransactionType.Authorization ||
    effectiveType === ('Transaction' as TransactionType);
  const normalizedAmount =
    amountPresentation === 'default' &&
    transaction?.type === TransactionType.Authorization &&
    (transaction?.amount ?? 0) > 0
      ? -(transaction?.amount ?? 0)
      : transaction?.amount;

  const handleExportPdf = async () => {
    if (!transaction?.id) return;
    const accountId = currentAccount?.id;
    if (accountId == null || !Number.isFinite(accountId)) {
      showError(t({ id: 'transactionDetails.error' }));
      return;
    }
    try {
      await downloadTransactionPdf(getTransactionPdf, {
        accountId,
        transactionId: transaction.id,
        language: userMeta?.language || language || 'EN',
      });

      showSuccess(t({ id: 'transactionDetails.success' }));
    } catch (error) {
      console.error('Failed to download PDF:', error);
      showError(t({ id: 'transactionDetails.error' }));
    }
  };

  return (
    <Modal
      centered
      closeIcon={<CloseOutlined id='btn__transaction-close' />}
      open={transaction !== undefined}
      onCancel={onClose}
      className={classes['transaction-modal']}
      styles={{
        content: {
          minHeight: 400
        },
        footer: {
          display: 'flex',
          justifyContent: 'center',
          paddingBottom: 20
        }
      }}
      footer={null}
    >
      {(loading || !transaction || !transactionDetails) && (
        <div
          style={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            top: 0,
            left: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 100
          }}
        >
          <CenteredContainer>
            <ActivityIndicator size="large" />
          </CenteredContainer>
        </div>
      )}

      {transaction && transactionDetails && (
        <Flex vertical gap={16} justify='center' className={classes['transaction-details']}>
          <Text className={classes['transaction-details__header']}>
            {transactionDetails?.created || transactionDetails?.origin_created}
          </Text>
          <Flex vertical className={classes['transaction-details__body']}>
            <Flex vertical align="center" gap={8}>
              <div className={classes['icon-wrap']}>
                <TransactionIcon size={70} type={transaction.type} status={transaction.status} />
              </div>
              <Flex vertical gap={4}>
                <Text style={{ fontSize: 12 }}>
                  {_.startCase(
                    transactionDetails?.description || transactionDetails?.details?.description
                  )}
                </Text>
                <Title level={2} style={{ textAlign: 'center', fontWeight: 500 }}>
                  {normalizedAmount != null
                    ? formatTransactionAmount(normalizedAmount, amountPresentation)
                    : ''}
                </Title>
              </Flex>
            </Flex>
            <Divider style={{ margin: '20px 0 20px' }} />
            <Title
              level={5}
              style={{
                marginBottom: 12,
                fontWeight: 600
              }}
            >
              {t({ id: 'transactionDetails.details' })}
            </Title>
            <Flex gap={12} vertical>
              {effectiveType === TransactionType.AccountAdjustment && payerName && (
                <Flex vertical>
                  <Text style={{ fontSize: 12, opacity: 0.5 }}>{t({ id: 'transactionDetails.payer' })}</Text>
                  <Text>{payerName}</Text>
                </Flex>
              )}
              {isMerchantType && (
                <Flex gap={10} vertical>
                  {transaction.recipientName && (
                    <Flex vertical>
                      <Text style={{ fontSize: 12, opacity: 0.5 }}>{t({ id: 'transactionDetails.merchant' })}</Text>
                      <Text>{transaction.recipientName}</Text>
                    </Flex>
                  )}
                  {transaction.terminalId && (
                    <Flex vertical>
                      <Text style={{ fontSize: 12, opacity: 0.5 }}>M{t({ id: 'transactionDetails.merchantId' })}</Text>
                      <Text>{transaction.terminalId}</Text>
                    </Flex>
                  )}
                  {transaction.merchantCountryCode && transaction.merchantCity && (
                    <Flex vertical>
                      <Text style={{ fontSize: 12, opacity: 0.5 }}>{t({ id: 'transactionDetails.merchantAddress' })}</Text>
                      <Text>
                        {transaction.merchantCountryCode}, {transaction.merchantCity}
                      </Text>
                    </Flex>
                  )}
                  {transaction.mcc && (
                    <Flex vertical>
                      <Text style={{ fontSize: 12, opacity: 0.5 }}>MCC</Text>
                      <Text>{transaction.mcc}</Text>
                    </Flex>
                  )}
                </Flex>
              )}
              {transaction.status && (
                <Flex vertical>
                  <Text style={{ fontSize: 12, opacity: 0.5 }}>{t({ id: 'transactionDetails.status' })}</Text>
                  <Text>{transaction.status}</Text>
                </Flex>
              )}
              {transactionDetails?.account && (
                <Flex vertical>
                  <Text style={{ fontSize: 12, opacity: 0.5 }}>{t({ id: 'transactionDetails.account' })}</Text>
                  <Text>
                    {transactionDetails.account.title}{' '}
                    <Text>({transactionDetails.account.iban})</Text>
                  </Text>
                </Flex>
              )}
              {transactionDetails?.card && (
                <Flex justify="space-between" align="center">
                  <Flex vertical>
                    <Text style={{ fontSize: 12, opacity: 0.5 }}>{t({ id: 'transactionDetails.card' })}</Text>
                    <Text>
                      {transactionDetails?.card.type === 'virtual'
                        ? t({ id: 'card.virtual' })
                        : t({ id: 'card.plastic' })}{' '}
                      *{transactionDetails?.card.number_masked.slice(-4)}
                    </Text>
                  </Flex>
                  <CardIcon size="s" cardNumber={transactionDetails?.card.number_masked} />
                </Flex>
              )}
              {effectiveType && (
                <Flex vertical>
                  <Text style={{ fontSize: 12, opacity: 0.5 }}>{t({ id: 'transactionDetails.type' })}</Text>
                  <Text>{_.startCase(effectiveType)}</Text>
                </Flex>
              )}
            </Flex>
            {transaction?.status !== TransactionStatus.Declined && (
              <Button
                disabled={loading}
                type="primary"
                size="small"
                style={{ marginTop: 32, backgroundColor: 'black' }}
                id="btn__download-transaction"
                onClick={handleExportPdf}
              >
                {t({ id: 'screens.history.single.buttons.pdf' })}
              </Button>
            )}
          </Flex>
        </Flex>
      )}
    </Modal>
  );
};

export default TransactionDetailsModal;
